import { useState } from "react";
import { useSWRConfig } from "swr";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { GoogleCalendarIcon } from "@/components/icons/GoogleCalendarIcon";
import { SectionBlock } from "@/components/workspace-settings/SectionBlock";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useGcalCallbackToast } from "@/hooks/useGcalCallbackToast";
import {
	disconnectCalendar,
	getCalendarStatus,
	gcalStatusKey,
	isGcalEventsKey,
	startCalendarConnect,
} from "@/services/google-calendar.service";

/** Consent returns the browser here, so the card can show the new state. */
const RETURN_TO = "/profile";

export function ProfileCalendarSection() {
	const { mutate: globalMutate } = useSWRConfig();
	const { data, error, isLoading, mutate } = useApiSWR(
		gcalStatusKey,
		getCalendarStatus,
	);
	const [busy, setBusy] = useState(false);

	useGcalCallbackToast();

	async function handleConnect() {
		setBusy(true);
		try {
			window.location.href = await startCalendarConnect(RETURN_TO);
		} catch {
			toast.error("Could not start the Google connection.");
			setBusy(false);
		}
	}

	async function handleDisconnect() {
		const confirmed = window.confirm(
			"Disconnect Google Calendar? TaskHub will stop showing your events and can no longer add tasks to your calendar. Events already created stay in Google.",
		);
		if (!confirmed) return;

		setBusy(true);
		try {
			await disconnectCalendar();
			await mutate();
			// Cached month windows would otherwise keep showing events we can no
			// longer refetch.
			await globalMutate(isGcalEventsKey);
			toast.success("Google Calendar disconnected.");
		} catch {
			toast.error("Could not disconnect Google Calendar.");
		} finally {
			setBusy(false);
		}
	}

	if (isLoading) {
		return (
			<Card className="p-4 sm:p-6">
				<Skeleton className="mb-4 h-5 w-40" />
				<Skeleton className="h-16 w-full" />
			</Card>
		);
	}

	if (error || !data) {
		return (
			<Card className="p-4 sm:p-6">
				<p className="text-sm text-muted-foreground">
					Failed to load your Google Calendar connection. Please try
					again later.
				</p>
			</Card>
		);
	}

	return (
		<SectionBlock
			title="Google Calendar"
			description="See your Google events next to your tasks, and add tasks to your calendar."
		>
			{data.connected ? (
				<div className="flex flex-col gap-3 max-w-xl">
					<div className="flex items-center gap-2 rounded-lg border border-border bg-muted-subtle/40 p-3">
						<CheckCircle2 className="h-4 w-4 shrink-0 text-secondary" />
						<p className="text-sm font-medium text-foreground">
							Connected
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={handleDisconnect}
						disabled={busy}
						className="self-start"
					>
						{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
						Disconnect
					</Button>
				</div>
			) : (
				<div className="flex flex-col gap-3 max-w-xl">
					<div className="flex items-start gap-3 rounded-lg border border-border bg-muted-subtle/40 p-3">
						<GoogleCalendarIcon className="mt-0.5 h-5 w-5 shrink-0" />
						<div className="min-w-0">
							<p className="text-sm font-medium text-foreground">
								Not connected
							</p>
							<p className="mt-0.5 text-xs text-muted-foreground">
								Connect your Google account to show your events
								on the task calendar.
							</p>
						</div>
					</div>
					<Button
						size="sm"
						onClick={handleConnect}
						disabled={busy}
						className="self-start"
					>
						{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
						Connect Google Calendar
					</Button>
				</div>
			)}
		</SectionBlock>
	);
}
